import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/** How long the confirmation stays up before dismissing itself. */
const DISPLAY_DURATION = 6000;

/**
 * "Item added" confirmation.
 *
 * Exists because the cart drawer no longer opens on every add — without it a successful add
 * is only reported by the fly-to-cart animation and the badge, neither of which says WHAT was
 * added. Shows the line the shopper just added and offers to reverse it.
 *
 * Deliberately silent rather than approximate: if the added line cannot be identified in the
 * cart payload it renders nothing, because naming the wrong product is worse than naming none.
 */
class AddedToast extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  /** @type {string | null} Line key of the add being reported, for the undo. */
  #lineKey = null;
  /** @type {number} Quantity this add contributed, which is what undo takes back off. */
  #addedQuantity = 0;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.querySelector('[data-toast-undo]')?.addEventListener('click', this.#undo);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    clearTimeout(this.#timer);
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    // A rejected add dispatches this event too, so other surfaces can roll back their
    // optimistic success state. Nothing entered the cart, so there is nothing to confirm.
    if (data.didError) return;

    // CartUpdateEvent shares this event name with CartAddEvent, so without this a cart
    // quantity change or a discount update would pop an "Item added" confirmation.
    if (data.source !== 'product-form-component') return;

    // The drawer is its own, larger confirmation — stacking a toast over it is noise.
    if (document.querySelector('cart-drawer-component dialog[open]')) return;

    const cart = event.detail.resource;
    const variantId = String(data.variantId ?? '');
    const item = cart?.items?.find(
      (/** @type {{ variant_id: number }} */ line) => String(line.variant_id) === variantId
    );

    // No reliable match — stay quiet rather than guess at a line.
    if (!item || !variantId) return;

    this.#show(item, Number(data.itemCount) || 1);
  };

  /**
   * @param {Record<string, any>} item - The cart line that was just added to.
   * @param {number} addedQuantity
   */
  #show(item, addedQuantity) {
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-toast-image]'));
    const title = this.querySelector('[data-toast-title]');
    const variant = this.querySelector('[data-toast-variant]');
    const price = this.querySelector('[data-toast-price]');

    if (image) {
      const src = item.image || item.featured_image?.url;
      if (src) {
        // Request it at render size rather than shipping the full cart image.
        image.src = String(src).replace(/(\.(?:jpe?g|png|webp|gif))/i, '_144x$1');
        image.alt = '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (title) title.textContent = item.product_title ?? item.title ?? '';

    if (variant) {
      // options_with_values reads "Color: Wine, Size: XS"; fall back to variant_title, and
      // drop the line entirely for single-variant products where it would say "Default Title".
      // One option per line ("Color: Wine" above "Size: XS"). Joined with a newline and
      // rendered under white-space: pre-line, because inline whitespace collapses in HTML
      // and ran the two together as "Color: Wine Size: XS".
      const options = Array.isArray(item.options_with_values)
        ? item.options_with_values
            .filter((/** @type {{ name: string, value: string }} */ o) => o?.name && o?.value)
            .map((/** @type {{ name: string, value: string }} */ o) => `${o.name}: ${o.value}`)
            .join('\n')
        : '';
      const fallback = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      variant.textContent = options || fallback;
      /** @type {HTMLElement} */ (variant).hidden = !(options || fallback);
    }

    if (price) price.textContent = this.#formatMoney(item.final_price ?? item.price);

    this.#lineKey = item.key ?? null;
    this.#addedQuantity = addedQuantity;

    const undo = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-toast-undo]'));
    if (undo) {
      undo.disabled = false;
      // Without a line key there is nothing safe to reverse, so do not offer it.
      undo.hidden = !this.#lineKey;
    }

    // Sit under the header rather than over it. Measured per show instead of hardcoded: the
    // header is sticky, and its height changes with the announcement bar and between
    // breakpoints — a fixed offset covered the logo on mobile.
    const header = document.querySelector('#header-component') || document.querySelector('header');
    const headerBottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--added-toast-top', `${headerBottom + 12}px`);

    this.hidden = false;
    // Next frame so the transition has a hidden->visible change to animate from.
    requestAnimationFrame(() => this.setAttribute('data-visible', 'true'));

    clearTimeout(this.#timer);
    this.#timer = setTimeout(this.#hide, DISPLAY_DURATION);
  }

  #hide = () => {
    clearTimeout(this.#timer);
    this.removeAttribute('data-visible');
    // Kept in the tree until the fade finishes, then taken out of the a11y tree entirely.
    this.#timer = setTimeout(() => {
      this.hidden = true;
    }, 300);
  };

  /**
   * Reverses the add by taking its quantity back off the line, rather than removing the line
   * outright — the shopper may have already had some of this variant in the cart before.
   */
  #undo = async () => {
    const key = this.#lineKey;
    if (!key) return;

    const undo = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-toast-undo]'));
    if (undo) undo.disabled = true;
    clearTimeout(this.#timer);

    try {
      // Re-read rather than trusting the quantity captured at show time: the shopper may have
      // changed this line in the drawer in the meantime.
      const current = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json());
      const line = current.items?.find((/** @type {{ key: string }} */ i) => i.key === key);
      if (!line) return;

      const nextQuantity = Math.max(0, line.quantity - this.#addedQuantity);

      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: nextQuantity }),
      });
      if (!response.ok) throw new Error(`Undo failed: ${response.status}`);

      const cart = await response.json();

      // Tells the badge, the drawer and the cart section the cart moved. Not flagged as
      // didError — this is a deliberate, successful change.
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'added-toast', {
          source: 'added-toast',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      // Leave the cart as the server has it; the shopper can still adjust it in the drawer.
      console.error(error);
      if (undo) undo.disabled = false;
    } finally {
      this.#hide();
    }
  };

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (typeof cents !== 'number') return '';

    // Follows the storefront's active currency rather than hardcoding a symbol, so the
    // confirmation matches what the rest of the cart shows under a currency switch.
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('added-toast')) {
  customElements.define('added-toast', AddedToast);
}
